/**
 * SmartAdvisorPanel – calls the FastAPI Orchestrator
 * and renders multi-agent explanations + recommended actions.
 */
import { useState } from 'react'
import { postWealthOrchestrator } from '@/lib/api'
import type { OrchestratorResponse } from '@/lib/types'
import { Bot, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react'

const INTENTS = [
  { id: 'what_now',        label: 'What should I do now?' },
  { id: 'rebalance',       label: 'Smart Rebalance' },
  { id: 'risk_review',     label: 'Full Risk Review' },
  { id: 'goals_optimize',  label: 'Optimise for Goals' },
]

export default function SmartAdvisorPanel() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<OrchestratorResponse | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [activeIntent, setActiveIntent] = useState<string | null>(null)

  const runIntent = async (intent: string) => {
    setLoading(true)
    setError(null)
    setResult(null)
    setActiveIntent(intent)
    try {
      const res = await postWealthOrchestrator({
        user_id: 'unman_lai_001',
        intent,
      })
      setResult(res)
    } catch (e) {
      setError(String(e))
    } finally { setLoading(false) }
  }

  return (
    <section className="card p-5 space-y-4" aria-label="Smart Advisor panel">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-purple/20 border border-purple/30 flex items-center justify-center">
          <Bot size={16} className="text-purple-light" aria-hidden />
        </div>
        <h2 className="font-semibold text-text-primary">Smart Advisor</h2>
        <span className="badge badge-blue ml-auto">FastAPI</span>
      </div>

      {/* Intent buttons */}
      <div className="flex flex-wrap gap-2">
        {INTENTS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => runIntent(id)}
            disabled={loading}
            aria-pressed={activeIntent === id}
            className={`btn-secondary text-xs py-1.5 transition-colors ${
              activeIntent === id && !loading ? 'border-blue text-blue-light' : ''
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 size={16} className="animate-spin text-blue" aria-hidden />
          Orchestrating agents…
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="flex items-start gap-2 p-3 bg-rose/10 border border-rose/20 rounded-lg text-sm text-rose-light" role="alert">
          <AlertCircle size={14} className="shrink-0 mt-0.5" aria-hidden />
          {error}
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <div className="space-y-4 animate-fade-in">
          {/* Summary */}
          <div className="p-3 bg-surface-DEFAULT rounded-lg">
            <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Summary</p>
            <p className="text-sm text-text-primary leading-relaxed">{result.summary}</p>
          </div>

          {/* Actions */}
          {result.recommended_actions.length > 0 && (
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Recommended actions</p>
              <ul className="space-y-2">
                {result.recommended_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-blue/10 text-blue-light flex items-center justify-center text-xs font-bold shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-text-secondary">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Agent explanations */}
          {Object.keys(result.explanations_per_agent).length > 0 && (
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Agent reasoning</p>
              <div className="space-y-1.5">
                {Object.entries(result.explanations_per_agent).map(([agent, explanation]) => {
                  const open = expanded[agent]
                  return (
                    <div key={agent} className="border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpanded(e => ({ ...e, [agent]: !e[agent] }))}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-left
                                   hover:bg-surface-hover transition-colors"
                        aria-expanded={open}
                      >
                        <span className="font-medium text-text-primary capitalize">{agent} agent</span>
                        {open ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                      </button>
                      {open && (
                        <div className="px-3 pb-3 text-xs text-text-secondary leading-relaxed border-t border-border pt-2">
                          {explanation}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
